import { AsyncStatus, useConfirm } from '@frontend/mui'
import { Check, PriorityHigh } from '@mui/icons-material'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { useEffectOnce } from 'react-use'
import { useRecoilState } from 'recoil'
import { authTokenStorage, logout } from 'src/context/apollo'
import { accountExistsQuery } from 'src/generated/apollo'
import { clearCookie } from 'src/utils/chaos'
import { wxWorkState } from './useWxWork'
import { useIntl } from '@frontend/t7nd'

export const WxWorkBindMessages = {
  USER_ALREADY_BOUND: {
    id: '用户已被绑定',
    defaultMessage: '用户已被绑定',
  },
  BIND_FAILED: {
    id: '绑定失败',
    defaultMessage: '绑定失败',
  },
  UNBIND_FAILED: {
    id: '取消绑定失败',
    defaultMessage: '取消绑定失败',
  },
  CONFIRM_UNBIND: {
    id: '确定取消绑定吗？',
    defaultMessage: '确定取消绑定吗？',
  },
  RETURN: {
    id: '返回',
    defaultMessage: '返回',
  },
  CONFIRM_CANCEL: {
    id: '确定取消',
    defaultMessage: '确定取消',
  },
  BIND_SUCCESS: {
    id: '绑定成功',
    defaultMessage: '绑定成功',
  },
  ACCOUNT_INVALID: {
    id: '账号失效',
    defaultMessage: '账号失效',
  },
  HELLO_BIND_SUCCESS: {
    id: '{account} 您好！您已绑定成功，可接收来自Teletraan的推送消息！',
    defaultMessage:
      '{account} 您好！您已绑定成功，可接收来自Teletraan的推送消息！',
  },
  ACCOUNT_DELETED: {
    id: '您原绑定的账号已被删除，\n请重新绑定！',
    defaultMessage: '您原绑定的账号已被删除，\n请重新绑定！',
  },
  CANCEL_BIND: {
    id: '取消绑定',
    defaultMessage: '取消绑定',
  },
  REBIND: {
    id: '重新绑定',
    defaultMessage: '重新绑定',
  },
} as const

export function WxWorkBindFailedPage() {
  const { formatMessage } = useIntl()
  const authToken = authTokenStorage.get()
  const confirm = useConfirm()
  const [wxWork, setWxWork] = useRecoilState(wxWorkState)
  const [account, setAccount] = useState('')
  const { bindStatus } = wxWork

  const handleAccount = useCallback(
    async (account: string) => {
      const { data } = await accountExistsQuery({
        variables: {
          account,
        },
      })
      if (data.accountExists) {
        setWxWork((prev) => ({
          ...prev,
          bindStatus: 'success',
          isBindSuccess: true,
        }))
      } else {
        setWxWork((prev) => ({ ...prev, bindStatus: 'invalid' }))
      }
    },
    [setWxWork],
  )

  const bind = useCallback(async () => {
    const bindUserResult = await fetch('/api/v1/wecom/binduser', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${authToken}`,
      },
    })
    if (bindUserResult.ok) {
      const { account } = await bindUserResult.json()
      setAccount(account)
      handleAccount(account)
    } else {
      if (bindUserResult.status === 400) {
        const confirmed = await confirm({
          title: formatMessage(WxWorkBindMessages.USER_ALREADY_BOUND),
        })
        if (confirmed) {
          logout()
        }
      } else if (bindUserResult.status === 401) {
        const confirmed = await confirm({
          title: formatMessage(WxWorkBindMessages.BIND_FAILED),
        })
        if (confirmed) {
          logout()
        }
      }
    }
  }, [authToken, confirm, handleAccount, formatMessage])

  const unbind = useCallback(async () => {
    const result = await fetch('/api/v1/wecom/unbinduser', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${authToken}`,
      },
    })
    if (!result.ok) {
      await confirm({ title: formatMessage(WxWorkBindMessages.UNBIND_FAILED) })
    }
    setWxWork({
      isBindSuccess: false,
      isAuthorized: false,
      bindStatus: null,
    })
    clearCookie()
    logout()
  }, [authToken, confirm, setWxWork, formatMessage])

  const onClickButton = useCallback(async () => {
    if (bindStatus === 'success') {
      const confirmed = await confirm({
        title: formatMessage(WxWorkBindMessages.CONFIRM_UNBIND),
        cancelLabel: formatMessage(WxWorkBindMessages.RETURN),
        confirmLabel: formatMessage(WxWorkBindMessages.CONFIRM_CANCEL),
      })
      if (confirmed) {
        unbind()
      }
    } else {
      unbind()
    }
  }, [bindStatus, confirm, unbind, formatMessage])

  useEffectOnce(() => {
    ;(async () => {
      if (bindStatus) return

      const userInfoResult = await fetch('/api/v1/wecom/userinfo')

      if (userInfoResult.ok) {
        userInfoResult.json().then(async (data) => {
          if (!data?.account) {
            // 未绑定进行绑定
            bind()
          } else {
            // 已绑定直接验证账号
            setAccount(data.account)
            handleAccount(data.account)
          }
        })
      } else {
        const confirmed = await confirm({
          title: formatMessage(WxWorkBindMessages.BIND_FAILED),
        })
        if (confirmed) {
          clearCookie()
          logout()
        }
      }
    })()
  })

  return (
    <AsyncStatus loading={!bindStatus}>
      <Stack sx={{ height: window.innerHeight }}>
        <Stack
          rowGap="10px"
          alignItems="center"
          justifyContent="center"
          height={700}
        >
          <Grid
            sx={{
              display: 'grid',
              width: '80px',
              height: '80px',
              borderRadius: '100%',
              background: bindStatus === 'success' ? '#30bf78' : '#f4664a',
              placeItems: 'center',
              color: 'white',
              fontSize: '48px',
              marginBottom: '14px',
            }}
          >
            {bindStatus === 'success' ? (
              <Check fontSize="inherit" />
            ) : (
              <PriorityHigh fontSize="inherit" />
            )}
          </Grid>
          <Typography variant="h4">
            {bindStatus === 'success'
              ? formatMessage(WxWorkBindMessages.BIND_SUCCESS)
              : bindStatus === 'invalid'
              ? formatMessage(WxWorkBindMessages.ACCOUNT_INVALID)
              : formatMessage(WxWorkBindMessages.BIND_FAILED)}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            whiteSpace="pre-wrap"
          >
            {bindStatus === 'success' &&
              formatMessage(WxWorkBindMessages.HELLO_BIND_SUCCESS, { account })}
            {bindStatus === 'invalid' &&
              formatMessage(WxWorkBindMessages.ACCOUNT_DELETED)}
          </Typography>
        </Stack>
        <Button onClick={() => onClickButton()} variant="text">
          {bindStatus === 'success'
            ? formatMessage(WxWorkBindMessages.CANCEL_BIND)
            : formatMessage(WxWorkBindMessages.REBIND)}
        </Button>
      </Stack>
    </AsyncStatus>
  )
}
